import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../utils/apiFetch";
import { formatDate } from "./dealFormat";
import { MARKETING_STATUS_CLASS, MARKETING_STATUS_LABELS } from "./marketingStatus";

import "./global/global.css";

const filterInputStyle = {
  padding: "6px 10px",
  fontSize: "13px",
  border: "1px solid #d0d0d0",
  borderRadius: "4px",
  outline: "none",
  minWidth: "160px",
  color: "#333",
  backgroundColor: "#fff",
};

const plural = (count, noun) => `${count} ${noun}${count !== 1 ? "s" : ""}`;

const MarketingManager = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterAddress, setFilterAddress] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/marketing/`
        );
        if (!response.ok) throw new Error("Failed to fetch marketing sessions");
        const body = await response.json();
        setSessions(body.sessions || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []);

  const filtered = sessions.filter(
    (session) =>
      (!filterAddress ||
        session.address.toLowerCase().includes(filterAddress.toLowerCase())) &&
      (!filterStatus || session.status === filterStatus)
  );

  const hasActiveFilter = filterAddress || filterStatus;
  const subtitle = hasActiveFilter
    ? `Showing ${filtered.length} of ${plural(sessions.length, "session")}`
    : "Your marketing sessions, flyers, and property sites.";

  let content;
  if (loading) {
    content = <p className="loading">Loading...</p>;
  } else if (error) {
    content = <p className="error">Error: {error}</p>;
  } else if (sessions.length === 0) {
    content = (
      <p className="marketing-empty">
        Nothing here yet. Start marketing a property by emailing the marketing manager.
      </p>
    );
  } else if (filtered.length === 0) {
    content = (
      <p style={{ color: "#777", fontSize: "14px" }}>
        No sessions match the current filters.
      </p>
    );
  } else {
    content = (
      <table className="cl-styled-table">
        <thead>
          <tr>
            <th>Property Address</th>
            <th>Status</th>
            <th style={{ textAlign: "center" }}>Flyers</th>
            <th style={{ textAlign: "center" }}>Emails</th>
            <th>Created</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((session) => (
            <tr key={session.id}>
              <td style={{ fontWeight: 500, color: "#333" }}>
                <Link to={`/marketing/${session.id}`} style={{ color: "inherit" }}>
                  {session.address || "Address not yet provided"}
                </Link>
              </td>
              <td>
                <span className={MARKETING_STATUS_CLASS[session.status] || ""}>
                  {MARKETING_STATUS_LABELS[session.status] || session.status}
                </span>
              </td>
              <td style={{ textAlign: "center" }}>{session.flyer_count}</td>
              <td style={{ textAlign: "center" }}>{session.email_count}</td>
              <td>{formatDate(session.created_at)}</td>
              <td>{formatDate(session.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="client-list-root">
      <div className="client-list-container">
        <div
          className="client-list-heading"
          style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}
        >
          <div className="lc">
            <h3 className="cl-rh">Marketing Manager</h3>
            <p className="cl-rh-sub">{subtitle}</p>
          </div>
        </div>

        {sessions.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              padding: "16px 20px 0",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="Filter by address..."
              value={filterAddress}
              onChange={(e) => setFilterAddress(e.target.value)}
              style={filterInputStyle}
            />
            <select
              aria-label="Filter by status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={filterInputStyle}
            >
              <option value="">All Statuses</option>
              {Object.entries(MARKETING_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="cl-table-container">{content}</div>
      </div>
    </div>
  );
};

export default MarketingManager;
